import React, { createContext, useState, useEffect, useContext } from 'react';
import { signInWithCustomToken, getIdTokenResult } from 'firebase/auth';
import { auth as firebaseAuth } from '../config/firebaseApp'; // The minimal firebase app for client-side auth operations
import * as authService from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('authToken'));
    const [isLoading, setIsLoading] = useState(true);
    const [authError, setAuthError] = useState(null);

    // Function to process user and token, centralizing logic
    const processUserAndToken = async (firebaseUser, idToken) => {
        if (firebaseUser && idToken) {
            setToken(idToken);
            localStorage.setItem('authToken', idToken);

            // Get custom claims from the ID token
            const tokenResult = await getIdTokenResult(firebaseUser);
            const isAdminClaim = tokenResult.claims.admin === true;

            // Fetch detailed user profile from backend (which should include custom 'verified' status)
            const { user: backendUser, error } = await authService.getCurrentUser(idToken);
                    if (error) {
                console.error('[AuthContext] Error fetching user details from backend:', error);
                setAuthError(error.message || 'Failed to fetch user details.');
                        setCurrentUser(null);
                        localStorage.removeItem('currentUser');
                return null;
            }
            
            // Combine Firebase user, claims, and backend profile data
            const comprehensiveUser = {
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                emailVerified: firebaseUser.emailVerified, // Standard Firebase email verification
                displayName: backendUser?.displayUsername || firebaseUser.displayName, // Prefer backend, fallback
                isAdmin: isAdminClaim, // From token claims
                is_verified_student: backendUser?.is_verified_student, // Custom verified status from backend/Firestore
                //... spread other relevant fields from backendUser if necessary
                ...(backendUser && typeof backendUser === 'object' ? backendUser : {}),
            };
            setCurrentUser(comprehensiveUser);
            localStorage.setItem('currentUser', JSON.stringify(comprehensiveUser));
            return comprehensiveUser;
                    } else {
                    setCurrentUser(null);
            setToken(null);
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('currentUser');
            return null;
        }
    };

    useEffect(() => {
        // Listen to Firebase Auth state changes directly if possible, or initialize from token
        const unsubscribe = firebaseAuth.onAuthStateChanged(async (user) => {
            setIsLoading(true);
            setAuthError(null);
            if (user) {
                const idToken = await user.getIdToken();
                await processUserAndToken(user, idToken);
            } else {
                // No user, clear state
                await processUserAndToken(null, null);
            }
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleSignInWithCustomToken = async (customTokenFromServer) => {
        setIsLoading(true);
        setAuthError(null);
        try {
            const userCredential = await signInWithCustomToken(firebaseAuth, customTokenFromServer);
            const idToken = await userCredential.user.getIdToken();
            const processedUser = await processUserAndToken(userCredential.user, idToken);
                setIsLoading(false);
            if (processedUser) {
                return { success: true, user: processedUser, token: idToken };
            }
            return { success: false, error: authError || 'Failed to process user after custom sign-in.' };
        } catch (error) {
            console.error("Error signing in with custom token:", error);
            setAuthError(error.message || 'Failed to sign in with custom token.');
            setCurrentUser(null); // Ensure user is null on error
            setToken(null);      // Ensure token is null on error
            localStorage.removeItem('authToken');
            localStorage.removeItem('currentUser');
            setIsLoading(false);
            return { success: false, error: error.message || 'Failed to sign in with custom token.' };
        }
    };

    const registerAndLogin = async (email, password, username, displayName) => {
        setIsLoading(true);
        setAuthError(null);
        try {
            const result = await authService.registerWithEmailAndPassword(email, password, username, displayName);
            if (result.success && result.customToken) {
                return await handleSignInWithCustomToken(result.customToken);
            }
            setAuthError(result.error || 'Registration failed (custom token issue)');
                setIsLoading(false);
            return { success: false, error: result.error || 'Registration failed (custom token issue)' };
        } catch (error) {
            console.error("Registration error:", error);
            setAuthError(error.message || 'An unexpected error occurred during registration.');
            setIsLoading(false);
            return { success: false, error: error.message || 'An unexpected error occurred during registration.' };
        }
    };

    const login = async (email, password) => {
        setIsLoading(true);
        setAuthError(null);
        try {
            // authService.loginWithEmailAndPassword should ideally return a custom token to be consistent,
            // or directly an ID token which then triggers onAuthStateChanged or calls processUserAndToken.
            // For now, assuming it might directly give a Firebase user and ID token after backend verification.
            const result = await authService.loginWithEmailAndPassword(email, password);
            if (result.success && result.token && result.userFromFirebaseAuth) { // Assuming userFromFirebaseAuth is firebase.User
                // If authService.login does a full Firebase client login, onAuthStateChanged will handle it.
                // If it just gives us a token and user data, we might need to manually process.
                // Best if backend returns a custom token, and we use handleSignInWithCustomToken.
                // OR if it just returns the firebase ID token and onAuthStateChanged is allowed to pick it up.

                // Quick fix if onAuthStateChanged doesn't pick up immediately after this custom login flow:
                // Re-initialize user from firebaseAuth.currentUser after backend login indicates success.
                const fbUser = firebaseAuth.currentUser;
                if (fbUser) {
                    const idToken = await fbUser.getIdToken();
                    const processedUser = await processUserAndToken(fbUser, idToken);
                    setIsLoading(false);
                    if(processedUser) return { success: true, user: processedUser, token: idToken };
                }
                // Fallback if currentUser not immediately available (should be rare if backend authenticates with Firebase)
                setAuthError(result.error || 'Login succeeded but failed to sync Firebase session.');
                setIsLoading(false);
                return { success: false, error: result.error || 'Login succeeded but failed to sync Firebase session.' };
            } else if (result.success && result.customToken) { // If backend login returns a custom token
                 return await handleSignInWithCustomToken(result.customToken);
            }
                setAuthError(result.error || 'Login failed');
                setIsLoading(false);
                return { success: false, error: result.error || 'Login failed' };
        } catch (error) {
            console.error("Login error:", error);
            setAuthError(error.message || 'An unexpected error occurred during login.');
            setIsLoading(false);
            return { success: false, error: error.message || 'An unexpected error occurred during login.' };
        }
    };
    
    const googleLogin = async (googleIdTokenFromClient) => {
        setIsLoading(true);
        setAuthError(null);
        try {
            const result = await authService.signInWithGoogleToken(googleIdTokenFromClient);
            if (result.success && result.customToken) {
                return await handleSignInWithCustomToken(result.customToken);
            }
                setAuthError(result.error || 'Google Sign-In failed.');
                setIsLoading(false);
                return { success: false, error: result.error || 'Google Sign-In failed.' };
        } catch (error) {
            console.error("Google login error in context:", error);
            setAuthError(error.message || 'An unexpected error occurred during Google Sign-In.');
            setIsLoading(false);
            return { success: false, error: error.message || 'An unexpected error occurred during Google Sign-In.' };
        }
    };

    const logout = async () => {
        setIsLoading(true);
        setAuthError(null);
        const currentTokenForLogout = localStorage.getItem('authToken');
        try {
            if(currentTokenForLogout){
                 await authService.logoutUser(currentTokenForLogout);
            }
            await firebaseAuth.signOut(); // Sign out from Firebase client SDK
        } catch (error) {
            console.error("Logout error (backend or Firebase client):", error);
            setAuthError("Error during logout, but client session cleared."); 
        } finally {
            // onAuthStateChanged will handle setting currentUser to null
            // Explicitly clear here as a safeguard and for immediate UI update if needed
            setCurrentUser(null);
            setToken(null);
            localStorage.removeItem('authToken');
            localStorage.removeItem('currentUser');
            setIsLoading(false);
        }
    };

    const value = {
        currentUser,
        idToken: token,
        isLoading,
        authError,
        registerAndLogin,
        login,
        googleLogin,
        logout,
        // handleSignInWithCustomToken // No longer need to expose if all auth flows use it internally
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 